import {
  QueryClient,
  useIsFetching,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect } from "react";
import {
  GetPortraitResponse,
  ListPortraitsResponse,
  PortraitSummary,
} from "tng-address-collection-shared/types/portraitsApiTypes.ts";
import { useAuthentication } from "../../../src/auth/useAuthentication.ts";
import { callPortraitApi } from "../portrait/portraitApi";
import { colleaguesPortrait } from "../portrait/portraitSubject";
import {
  IMAGE_LINK_RENEWAL_INTERVAL,
  portraitQueryKey,
} from "../portrait/usePortrait";

const listQueryKey = ["admin", "portraits"] as const;

export const useListPortraits = () => {
  const { getToken, isAuthenticated } = useAuthentication();

  return useQuery({
    queryKey: listQueryKey,
    queryFn: async ({ signal }) =>
      callPortraitApi("/api/admin/portraits", ListPortraitsResponse, {
        token: await getToken(),
        signal,
      }),
    enabled: isAuthenticated,
    // Reloading signs every image anew, so only as often as the links need
    staleTime: IMAGE_LINK_RENEWAL_INTERVAL,
    refetchInterval: IMAGE_LINK_RENEWAL_INTERVAL,
  });
};

const updateGalleryEntry = (
  queryClient: QueryClient,
  uidNumber: number,
  update: (summary: PortraitSummary | undefined) => PortraitSummary | undefined,
) =>
  queryClient.setQueryData<ListPortraitsResponse>(listQueryKey, (list) =>
    list
      ? {
          employees: list.employees.map((employee) =>
            employee.uidNumber === uidNumber
              ? { ...employee, portrait: update(employee.portrait) }
              : employee,
          ),
        }
      : list,
  );

/** The gallery stays open next to a colleague's portrait, so it follows the changes made there */
export const useGalleryEntrySync = (
  uidNumber: number,
  portrait: GetPortraitResponse | undefined,
) => {
  const queryClient = useQueryClient();
  // A reload that started before a change here would undo it in the list.
  // Not the list's data, which this sync changes itself.
  const listReloads = useIsFetching({ queryKey: listQueryKey });

  useEffect(() => {
    if (!portrait) {
      return;
    }
    updateGalleryEntry(queryClient, uidNumber, (summary) =>
      summary || portrait.hasUpload
        ? {
            choice: portrait.choice,
            flagged: portrait.flagged,
            cutoutUrl: portrait.cutout?.imageUrl,
          }
        : summary,
    );
  }, [queryClient, uidNumber, portrait, listReloads]);
};

// Updates the one entry, as reloading the list signs every image anew
export const useFlagInGallery = () => {
  const { getToken } = useAuthentication();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      uidNumber,
      flagged,
    }: {
      uidNumber: number;
      flagged: boolean;
    }) =>
      callPortraitApi(
        `/api/admin/portraits/${uidNumber}/flag`,
        GetPortraitResponse,
        { token: await getToken(), body: { flagged } },
      ),
    // A read still under way would put the flag back as it was
    onMutate: async ({ uidNumber }) => {
      const listWasLoading =
        queryClient.isFetching({ queryKey: listQueryKey }) > 0;
      await Promise.all([
        queryClient.cancelQueries({ queryKey: listQueryKey }),
        queryClient.cancelQueries({
          queryKey: portraitQueryKey(colleaguesPortrait(uidNumber).apiPath),
        }),
      ]);
      return { listWasLoading };
    },
    onSuccess: (portrait, { uidNumber }) => {
      updateGalleryEntry(
        queryClient,
        uidNumber,
        (summary) => summary && { ...summary, flagged: portrait.flagged },
      );
      queryClient.setQueryData(
        portraitQueryKey(colleaguesPortrait(uidNumber).apiPath),
        portrait,
      );
    },
    // The cancelled reload would have renewed the image links
    onSettled: (_portrait, _error, _variables, onMutateResult) => {
      if (onMutateResult?.listWasLoading) {
        void queryClient.refetchQueries({ queryKey: listQueryKey });
      }
    },
  });
};
